/**
 * @fileOverview The Tagesmeldungen walkthrough. Pure data.
 * See docs/view-help.md and docs/view-help/daysubmissions.md.
 */

import { ENTITY_ICONS } from "@/services/entityIcons"
import { stepsOn } from "@/services/viewHelp/define"
import type { ViewHelpTour } from "@/services/viewHelp/types"

const onList = stepsOn("DaySubmissionOverview", { navEntry: "nav-daysubmission" })
const appScreen = (screen: string) => `view-help/daysubmissions-app-${screen}-{locale}.webp`
// Yesterday's, the open one with something still to settle (`samples/daysubmissions.ts`)
const onDay = stepsOn("DaySubmissionDetail", {
  navEntry: "nav-daysubmission",
  at: { params: { daySubmissionId: "ds-gestern" } },
})

/**
 * Tagesmeldungen: the days employees sent from the app, and what the office makes of them, then how such a
 * day is recorded and sent in the app. The Aktionen menu is opened; nothing in it is pressed: every
 * button here opens a report to make, or dismisses.
 */
export const DAY_SUBMISSIONS_TOUR: ViewHelpTour = {
  key: "daysubmissions",
  icon: ENTITY_ICONS.daysubmission,
  routes: ["DaySubmissionOverview", "DaySubmissionDetail"],
  scenes: [
    // The head's Aktionen menu, opened to show the decisions and closed with Escape
    { key: "actions", enter: [{ do: "click", target: "entity-actions" }], exit: [{ do: "escape" }] },
  ],
  steps: [
    // ── the list ─────────────────────────────────────────────────────────────
    onList("intro", "ds-table", ENTITY_ICONS.daysubmission, { placement: "top", zoom: false }),
    onList("filter", "ds-filters", "mdi-filter-variant", { placement: "bottom" }),
    onList("row", "row-ds-gestern", "mdi-gesture-tap", { placement: "bottom" }),

    // ── one day ──────────────────────────────────────────────────────────────
    // In the order the office reads it: the decisions bar, the day's head with its weather, the sender's
    // comments, the entries and a row a report of it lacks something of, where the day was recorded, and last
    // the menu that makes a report of it too.
    onDay("readiness", "ds-readiness", "mdi-clipboard-check-outline", { placement: "bottom" }),
    onDay("head", "ds-head", "mdi-calendar-account", { placement: "bottom" }),
    onDay("weather", "ds-weather", "mdi-weather-partly-cloudy", { placement: "bottom" }),
    onDay("comments", "ds-comments", "mdi-comment-text-outline", { placement: "bottom" }),
    onDay("entries", "ds-entries", "mdi-format-list-bulleted", { placement: "top", zoom: false }),
    onDay("gap", "ds-entry-crew-1", "mdi-pencil-plus-outline", { placement: "bottom" }),
    onDay("origin", "ds-origin", "mdi-map-marker-radius-outline", { placement: "left" }),
    onDay("actions", "ds-actions", "mdi-notebook-plus-outline", { placement: "left", scene: "actions" }),
    onDay("again", "view-help-trigger", "mdi-help-circle-outline", { placement: "bottom" }),

    // ── in the app ───────────────────────────────────────────────────────────
    // Where a Tagesmeldung comes from, as the walkthrough cannot show the app: its screens, rendered by
    // the mobile repo's `make view-help-screens` with the day `ds-gestern` was sent as, in the reader's
    // language. After the closing step, as another tour's Zusammenhänge are, so that the detail page's
    // steps stay one section; over that page, which stays behind them. See docs/view-help/daysubmissions.md.
    onDay("app-record", null, "mdi-microphone-outline", {
      section: "app",
      images: [
        { src: appScreen("start"), alt: "help.tour.daysubmissions.image.start" },
        { src: appScreen("listening"), alt: "help.tour.daysubmissions.image.listening" },
      ],
    }),
    onDay("app-review", null, "mdi-send-check-outline", {
      section: "app",
      images: [
        { src: appScreen("review"), alt: "help.tour.daysubmissions.image.review" },
        { src: appScreen("check"), alt: "help.tour.daysubmissions.image.check" },
      ],
    }),
    onDay("app-sent", null, "mdi-comment-text-outline", {
      section: "app",
      images: [
        { src: appScreen("sent"), alt: "help.tour.daysubmissions.image.sent" },
        { src: appScreen("sent-comments"), alt: "help.tour.daysubmissions.image.sentComments" },
      ],
    }),
  ],
}
